import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchTasks, updateTask } from "@/lib/api";

const statusColor = {
  "To Do": "bg-zinc-500/10 text-zinc-300 border-zinc-700",
  "In Progress": "bg-blue-500/10 text-blue-300 border-blue-500/30",
  Done: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
};

const Tasks = () => {
  const [tasks, setTasks] = useState(null);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    fetchTasks().then(setTasks).catch(() => setTasks([]));
  }, []);

  const assignees = useMemo(() => {
    if (!tasks) return ["All"];
    return ["All", ...Array.from(new Set(tasks.map((t) => t.assignee)))];
  }, [tasks]);

  const filtered = useMemo(() => {
    if (!tasks) return [];
    return filter === "All" ? tasks : tasks.filter((t) => t.assignee === filter);
  }, [tasks, filter]);

  const toggle = async (t) => {
    const next = t.status === "Done" ? "To Do" : "Done";
    const prev = tasks;
    setTasks((ts) => ts.map((x) => (x.id === t.id ? { ...x, status: next } : x)));
    try {
      await updateTask(t.id, { status: next });
    } catch (_e) {
      setTasks(prev);
      toast.error("Could not update task");
    }
  };

  if (tasks === null) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tasks</h1>
          <p className="text-sm text-zinc-500 mt-1">{filtered.length} tasks</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 bg-white/[0.03] border-white/10 text-zinc-100" data-testid="tasks-assignee-filter">
              <SelectValue placeholder="Assignee" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              {assignees.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button className="bg-blue-600 hover:bg-blue-500 text-white">+ New Task</Button>
        </div>
      </div>

      <div className="rounded-xl border border-white/8 bg-white/[0.02] divide-y divide-white/5" data-testid="tasks-list">
        {filtered.map((t) => (
          <div key={t.id} className="flex items-center gap-4 p-4 hover:bg-white/[0.02]" data-testid={`task-row-${t.id}`}>
            <Checkbox
              checked={t.status === "Done"}
              onCheckedChange={() => toggle(t)}
              className="border-white/30 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
              data-testid={`task-check-${t.id}`}
            />
            <div className="flex-1 min-w-0">
              <div className={`text-sm ${t.status === "Done" ? "text-zinc-500 line-through" : "text-zinc-100"}`}>{t.title}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{t.linked} · due {t.due}</div>
            </div>
            <span className="hidden sm:inline text-xs text-zinc-400">{t.assignee}</span>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${statusColor[t.status]}`}>{t.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Tasks;
